import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Bell, MessageSquare, Mail, Megaphone, ShieldOff, ShieldCheck, Loader2, RefreshCw, Wallet, AlertTriangle,
  Coins, RotateCcw, Save, CheckCircle2, XCircle, ShieldAlert, KeyRound, UploadCloud, Sparkles, Gauge,
  ShieldQuestion, ChevronRight, UserPlus, EyeOff,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { adminControlCenterApi, adminSiteApi } from "../lib/api";
import type { FinancialConfigData, FinancialConfigPatch, FinancialCaps } from "../lib/api";
import type { SiteSignup } from "@wedding/shared";
import { cn } from "../lib/utils";
import { AppLink } from "../lib/route-primitives";
import { paths } from "../lib/paths";

interface NotifConfig { sms_enabled: boolean; email_enabled: boolean; sms_campaigns_enabled: boolean; }
interface NotifStats {
  sms24h: number; email24h: number; failed24h: number;
  blocked24h: number; sms7d: number; email7d: number;
}
interface CampaignStats {
  activeCampaigns: number; sends24h: number; failed24h: number;
  sends7d: number; heldDue: number;
  /** Onay bekleyen kampanya sayisi (047) — kesfedilebilirlik karti. */
  pendingApproval: number;
}
interface SmsSpend {
  dailySegmentsUsed: number; dailyCap: number; estUsd: number;
  breakerTripped: boolean; trippedAt: string | null; trippedReason: string | null;
}
interface NotifLog {
  id: string; type: string; recipient: string; status: string;
  error: string | null; wedding_id: string | null; created_at: string;
}
type LogsResponse = { items: NotifLog[]; hasMore: boolean; nextCursor: string | null };

const STATUS_STYLE: Record<string, string> = {
  sent:    "bg-green-500/10 text-green-500 border-green-500/20",
  blocked: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  failed:  "bg-red-500/10 text-red-500 border-red-500/20",
};

// Flex-based toggle — translate yok, taşma riski yok
function Toggle({ checked, onChange, disabled }: {
  checked: boolean; onChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={cn(
        "flex items-center h-6 w-11 rounded-full border-2 p-0.5 shrink-0",
        "transition-[background-color,border-color] duration-300 cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
        "hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed",
        checked ? "bg-primary/30 border-primary/50 justify-end" : "bg-muted border-border justify-start"
      )}
    >
      <span className={cn(
        "h-4 w-4 rounded-full shadow-sm transition-colors duration-300 shrink-0",
        checked ? "bg-primary" : "bg-muted-foreground/40"
      )} />
    </button>
  );
}

// Toast — settings.tsx pattern (success + error), non-negotiable for every mutation
function Toast({ msg, ok, onDone }: { msg: string; ok: boolean; onDone: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 2800);
    return () => clearTimeout(timer);
  }, [onDone]);
  return (
    <div className={cn(
      "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200]",
      "flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium",
      ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white",
    )}>
      {ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
      <span>{msg}</span>
    </div>
  );
}

// ── Finansal cap alan tanımları ─────────────────────────────────────
const BYTES_PER_MB = 1048576;
type CapKey =
  | "verifyDailyCap" | "smsSegmentDailyCap" | "smsPreviewDailyCap"
  | "smsRoundRecipientHardCap" | "smsManualSegmentMaxGuests" | "emailDailyCap"
  | "smsTrPriceUsd" | "maxImageBytes" | "maxVideoDirectBytes"
  | "qrScanVisitorDailyCap" | "selfRegisterDailyCap" | "smsApprovalThreshold";

interface CapField {
  key: CapKey;
  kind: "int" | "float" | "mb"; // display/parse strategy
  min: number;                  // display-unit bound (MB for kind="mb")
  max: number;
  step: number;
  /** Render a `finance.capsDesc.<key>` hint — for caps whose label alone is ambiguous. */
  desc?: true;
}

// Bounds mirror the server Zod schema (server is authoritative; these are UX hints).
// maxImageBytes/maxVideoDirectBytes shown & edited in MB.
const CAP_FIELDS: CapField[] = [
  { key: "verifyDailyCap",            kind: "int",   min: 100, max: 50000,  step: 1 },
  { key: "smsSegmentDailyCap",        kind: "int",   min: 100, max: 100000, step: 1 },
  { key: "smsPreviewDailyCap",        kind: "int",   min: 0,   max: 100,    step: 1 },
  { key: "smsRoundRecipientHardCap",  kind: "int",   min: 1,   max: 10000,  step: 1 },
  { key: "smsManualSegmentMaxGuests", kind: "int",   min: 1,   max: 500,    step: 1 },
  { key: "emailDailyCap",             kind: "int",   min: 10,  max: 100000, step: 1 },
  { key: "smsTrPriceUsd",             kind: "float", min: 0,   max: 1,      step: 0.0001 },
  { key: "maxImageBytes",             kind: "mb",    min: 1,   max: 100,    step: 1 },
  { key: "maxVideoDirectBytes",       kind: "mb",    min: 10,  max: 5120,   step: 1 },
  { key: "qrScanVisitorDailyCap",     kind: "int",   min: 1,   max: 1000,   step: 1, desc: true },
  // Public QR/join linkinden düğün başına 24 saatlik kayıt tavanı.
  { key: "selfRegisterDailyCap",      kind: "int",   min: 1,   max: 5000,   step: 1, desc: true },
  // min 0 = "kilitleme modu" (her kampanya onay ister), max 5000 = fiilen devre disi.
  { key: "smsApprovalThreshold",      kind: "int",   min: 0,   max: 5000,   step: 1, desc: true },
];

// effective value (bytes for mb fields) → display string
function effToDisplay(field: CapField, caps: FinancialCaps): string {
  const v = caps[field.key] as number;
  if (field.kind === "mb") return String(Math.round(v / BYTES_PER_MB));
  return String(v);
}

// display string → server value (bytes for mb fields), or null if unparseable
function displayToServer(field: CapField, raw: string): number | null {
  const n = Number(raw.trim());
  if (raw.trim() === "" || !Number.isFinite(n)) return null;
  if (field.kind === "mb") return Math.round(n * BYTES_PER_MB);
  if (field.kind === "float") return n;
  return Math.round(n);
}

/**
 * Sunucudan gelen finansal config'i doğrula. Eksik/bozuk bir alt nesne
 * (overrides/monitoring/spendBreaker — KV'de elle bozulmuş değer vb.) render
 * sırasında TypeError atıp sayfayı beyaza düşürüyordu; artık güvenli varsayılan.
 */
export function normalizeFinConfig(raw: unknown): FinancialConfigData | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Partial<FinancialConfigData>;
  if (!r.effective || typeof r.effective !== "object") return null;
  const mon = (r.monitoring && typeof r.monitoring === "object" ? r.monitoring : {}) as Partial<FinancialConfigData["monitoring"]>;
  const breaker = mon.spendBreaker && typeof mon.spendBreaker === "object" ? mon.spendBreaker : { tripped: false };
  return {
    effective: r.effective,
    overrides: r.overrides && typeof r.overrides === "object" ? r.overrides : {},
    monitoring: {
      verifyUsedToday: Number(mon.verifyUsedToday ?? 0),
      verifyResetAt: mon.verifyResetAt ?? null,
      smsSegmentsUsedToday: Number(mon.smsSegmentsUsedToday ?? 0),
      spendBreaker: breaker,
    },
  };
}

// New /financial-config kill switches
type FinSwitchKey = "verifyEnabled" | "uploadsEnabled" | "imageTransformEnabled" | "smsApprovalEnabled" | "organizerSeesGuestDeletedMedia";

// Unified kill-switch descriptor for the consolidated top card. Two backends:
//  - src="config" → notifications_config via handleToggle
//  - src="fin"    → financial_limits via handleFinSwitch (danger-confirm for verify/uploads)
// Narrow on `src` before computing checked/onChange/disabled so key types stay sound.
type SwitchRow =
  | { src: "config"; key: keyof NotifConfig; icon: LucideIcon; color: string; label: string; desc: string; master?: boolean }
  | { src: "fin"; key: FinSwitchKey; icon: LucideIcon; color: string; label: string; desc: string; dangerous: boolean };

export function ControlCenterPage() {
  const { t, i18n } = useTranslation();

  const [config, setConfig]         = useState<NotifConfig | null>(null);
  const [stats, setStats]           = useState<NotifStats | null>(null);
  const [campaignStats, setCampaignStats] = useState<CampaignStats | null>(null);
  const [smsSpend, setSmsSpend]     = useState<SmsSpend | null>(null);
  const [resettingBreaker, setResettingBreaker] = useState(false);
  const [logs, setLogs]             = useState<NotifLog[]>([]);
  const [loading, setLoading]       = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [saveSuccess, setSaveSuccess]   = useState(false);
  const [logsLoading, setLogsLoading]   = useState(false);
  const [hasMore, setHasMore]           = useState(false);
  const [nextCursor, setNextCursor]     = useState<string | null>(null);
  const [typeFilter, setTypeFilter]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // ── Finansal Kontroller ──────────────────────────────────
  const [finConfig, setFinConfig]   = useState<FinancialConfigData | null>(null);
  const [capForm, setCapForm]       = useState<Record<CapKey, string>>({} as Record<CapKey, string>);
  const [savingCaps, setSavingCaps] = useState(false);
  const [resettingCap, setResettingCap] = useState<CapKey | null>(null);
  const [savingSwitch, setSavingSwitch] = useState<FinSwitchKey | null>(null);
  const [confirmSwitch, setConfirmSwitch] = useState<FinSwitchKey | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = useCallback((msg: string, ok = true) => setToast({ msg, ok }), []);

  // Seed the editable form from server truth ONCE (first load). After that
  // capForm is the user's working copy — a per-field reset or a switch toggle
  // reloads finConfig but must NOT clobber unsaved edits in OTHER cap fields, so
  // handlers reconcile capForm explicitly (see handleSaveCaps / handleResetCap).
  const seededRef = useRef(false);
  useEffect(() => {
    if (!finConfig || seededRef.current) return;
    const next = {} as Record<CapKey, string>;
    for (const f of CAP_FIELDS) next[f.key] = effToDisplay(f, finConfig.effective);
    setCapForm(next);
    seededRef.current = true;
  }, [finConfig]);

  const capBaseline = useMemo(() => {
    const b = {} as Record<CapKey, string>;
    if (finConfig) for (const f of CAP_FIELDS) b[f.key] = effToDisplay(f, finConfig.effective);
    return b;
  }, [finConfig]);

  const reloadFinConfig = useCallback(async (): Promise<FinancialConfigData | undefined> => {
    const res = await adminControlCenterApi.getFinancialConfig();
    const data = normalizeFinConfig(res.data) ?? undefined;
    if (data) setFinConfig(data);
    return data;
  }, []);

  // Organizatör kaydı (self-servis) — platform ayarı `site:signup.selfServe`.
  const [signup, setSignup] = useState<SiteSignup | null>(null);
  const selfServe = signup ? signup.selfServe : null;
  const [savingSelfServe, setSavingSelfServe] = useState(false);

  // Config + stats: her istek AYRI sonuçlanır (allSettled) — biri düşerse
  // diğer kartlar yine çizilir; düşenler için hata toast'u.
  const loadAll = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      adminControlCenterApi.getConfig(),
      adminControlCenterApi.getStats(),
      adminControlCenterApi.getSmsCampaignsStats(),
      adminControlCenterApi.getSmsSpend(),
      adminControlCenterApi.getFinancialConfig(),
      adminSiteApi.get(),
    ]);
    const [cfgRes, statsRes, campRes, spendRes, finRes, siteRes] = results;
    if (cfgRes.status === "fulfilled" && cfgRes.value?.data) setConfig(cfgRes.value.data as NotifConfig);
    if (statsRes.status === "fulfilled" && statsRes.value?.data) setStats(statsRes.value.data as NotifStats);
    if (campRes.status === "fulfilled" && campRes.value?.data) setCampaignStats(campRes.value.data as CampaignStats);
    if (spendRes.status === "fulfilled" && spendRes.value?.data) setSmsSpend(spendRes.value.data as SmsSpend);
    if (finRes.status === "fulfilled") {
      const fin = normalizeFinConfig(finRes.value?.data);
      if (fin) setFinConfig(fin);
    }
    if (siteRes.status === "fulfilled" && siteRes.value?.data) setSignup(siteRes.value.data.signup);
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0) showToast(t("controlCenter.loadPartialFailed", { count: failed }), false);
    setLoading(false);
  }, [showToast, t]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Logs: when filters change
  const loadLogs = useCallback(async (type: string, status: string) => {
    setLogsLoading(true);
    setNextCursor(null);
    try {
      const p = new URLSearchParams({ limit: "25" });
      if (type)   p.set("type", type);
      if (status) p.set("status", status);
      const res = await adminControlCenterApi.getLogs(p);
      const d = res.data as LogsResponse;
      setLogs(d?.items ?? []);
      setHasMore(d?.hasMore ?? false);
      setNextCursor(d?.nextCursor ?? null);
    } catch (e) {
      showToast(e instanceof Error ? e.message : t("common.error"), false);
    }
    finally { setLogsLoading(false); }
  }, [showToast, t]);

  useEffect(() => { loadLogs(typeFilter, statusFilter); }, [loadLogs, typeFilter, statusFilter]);

  const loadMore = async () => {
    if (!nextCursor || logsLoading) return;
    setLogsLoading(true);
    try {
      const p = new URLSearchParams({ limit: "25" });
      if (typeFilter)   p.set("type", typeFilter);
      if (statusFilter) p.set("status", statusFilter);
      p.set("cursor", nextCursor);
      const res = await adminControlCenterApi.getLogs(p);
      const d = res.data as LogsResponse;
      setLogs((prev) => [...prev, ...(d?.items ?? [])]);
      setHasMore(d?.hasMore ?? false);
      setNextCursor(d?.nextCursor ?? null);
    } catch (e) {
      showToast(e instanceof Error ? e.message : t("common.error"), false);
    }
    finally { setLogsLoading(false); }
  };

  const handleToggle = async (key: "sms_enabled" | "email_enabled" | "sms_campaigns_enabled", value: boolean) => {
    if (!config || savingConfig) return;
    const prev = { ...config };
    const next = { ...config, [key]: value };
    setConfig(next);
    setSavingConfig(true);
    try {
      await adminControlCenterApi.patchConfig(next);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
      showToast(value ? t("finance.switchEnabled") : t("finance.switchDisabled"));
    } catch (e) {
      setConfig(prev);
      showToast(e instanceof Error ? e.message : t("common.error"), false);
    }
    finally { setSavingConfig(false); }
  };

  const handleSelfServe = async (value: boolean) => {
    if (savingSelfServe || !signup) return;
    if (!value && !window.confirm(t("siteAdmin.sales.closeConfirm"))) return;
    setSavingSelfServe(true);
    try {
      const next = { ...signup, selfServe: value };
      const res = await adminSiteApi.put("signup", next);
      setSignup(res.data ?? next);
      showToast(value ? t("finance.switchEnabled") : t("finance.switchDisabled"));
    } catch (e) {
      showToast(e instanceof Error ? e.message : t("common.error"), false);
    } finally {
      setSavingSelfServe(false);
    }
  };

  const refresh = () => {
    void loadAll();
    loadLogs(typeFilter, statusFilter);
  };

  const handleResetBreaker = async () => {
    if (resettingBreaker) return;
    setResettingBreaker(true);
    try {
      await adminControlCenterApi.resetSmsSpendBreaker();
      const res = await adminControlCenterApi.getSmsSpend();
      if (res.data) setSmsSpend(res.data as SmsSpend);
      showToast(t("controlCenter.spend.resetDone"));
    } catch (e) {
      showToast(e instanceof Error ? e.message : t("common.error"), false);
    }
    finally { setResettingBreaker(false); }
  };

  // Dirty-check: only fields differing from loaded effective, parsed & within client bounds.
  const capPatch = useMemo(() => {
    const patch: FinancialConfigPatch = {};
    if (!finConfig) return patch;
    for (const f of CAP_FIELDS) {
      if (capForm[f.key] === capBaseline[f.key]) continue;
      const v = displayToServer(f, capForm[f.key] ?? "");
      if (v === null || v < f.min * (f.kind === "mb" ? BYTES_PER_MB : 1) || v > f.max * (f.kind === "mb" ? BYTES_PER_MB : 1)) continue;
      patch[f.key] = v;
    }
    return patch;
  }, [finConfig, capForm, capBaseline]);

  // A dirty field whose value can't be parsed or is out of bounds → block save.
  const hasInvalidDirty = useMemo(() => {
    if (!finConfig) return false;
    return CAP_FIELDS.some((f) => {
      if (capForm[f.key] === capBaseline[f.key]) return false;
      const v = displayToServer(f, capForm[f.key] ?? "");
      const lo = f.min * (f.kind === "mb" ? BYTES_PER_MB : 1);
      const hi = f.max * (f.kind === "mb" ? BYTES_PER_MB : 1);
      return v === null || v < lo || v > hi;
    });
  }, [finConfig, capForm, capBaseline]);

  const capDirtyCount = Object.keys(capPatch).length;
  const canSaveCaps = capDirtyCount > 0 && !hasInvalidDirty && !savingCaps;

  const handleSaveCaps = async () => {
    if (!canSaveCaps) return;
    setSavingCaps(true);
    try {
      await adminControlCenterApi.patchFinancialConfig(capPatch);
      const data = await reloadFinConfig();
      // Full re-seed: every dirty field was just persisted, so working copy = server truth.
      if (data) {
        const next = {} as Record<CapKey, string>;
        for (const f of CAP_FIELDS) next[f.key] = effToDisplay(f, data.effective);
        setCapForm(next);
      }
      showToast(t("finance.saveSuccess"));
    } catch (e) {
      showToast(e instanceof Error ? e.message : t("finance.boundsError"), false);
    } finally {
      setSavingCaps(false);
    }
  };

  const handleResetCap = async (key: CapKey) => {
    if (resettingCap) return;
    setResettingCap(key);
    try {
      await adminControlCenterApi.patchFinancialConfig({ [key]: null });
      const data = await reloadFinConfig();
      // Reconcile ONLY the reset field → other fields' unsaved edits are preserved.
      if (data) {
        const f = CAP_FIELDS.find((x) => x.key === key);
        if (f) setCapForm((prev) => ({ ...prev, [key]: effToDisplay(f, data.effective) }));
      }
      showToast(t("finance.resetSuccess"));
    } catch (e) {
      showToast(e instanceof Error ? e.message : t("common.error"), false);
    } finally {
      setResettingCap(null);
    }
  };

  const performSwitchPatch = async (key: FinSwitchKey, value: boolean) => {
    setSavingSwitch(key);
    try {
      await adminControlCenterApi.patchFinancialConfig({ [key]: value });
      await reloadFinConfig();
      showToast(value ? t("finance.switchEnabled") : t("finance.switchDisabled"));
    } catch (e) {
      showToast(e instanceof Error ? e.message : t("common.error"), false);
    } finally {
      setSavingSwitch(null);
    }
  };

  const handleFinSwitch = (key: FinSwitchKey, value: boolean) => {
    if (savingSwitch) return;
    // Turning OFF a dangerous switch → confirm first. Turning ON → no confirm.
    if (!value && (key === "verifyEnabled" || key === "uploadsEnabled" || key === "smsApprovalEnabled")) {
      setConfirmSwitch(key);
      return;
    }
    void performSwitchPatch(key, value);
  };

  const STAT_KEYS: { key: keyof NotifStats; label: string; color: string }[] = [
    { key: "sms24h",     label: t("controlCenter.sms24h"),     color: "text-blue-400" },
    { key: "email24h",   label: t("controlCenter.email24h"),   color: "text-indigo-400" },
    { key: "failed24h",  label: t("controlCenter.failed24h"),  color: "text-red-400" },
    { key: "blocked24h", label: t("controlCenter.blocked24h"), color: "text-amber-400" },
    { key: "sms7d",      label: t("controlCenter.sms7d"),      color: "text-blue-400" },
    { key: "email7d",    label: t("controlCenter.email7d"),    color: "text-indigo-400" },
  ];

  const TYPE_OPTS = [
    { value: "",      label: t("controlCenter.filters.all") },
    { value: "sms",   label: t("controlCenter.filters.sms") },
    { value: "email", label: t("controlCenter.filters.email") },
  ];
  const STATUS_OPTS = [
    { value: "",        label: t("controlCenter.filters.all") },
    { value: "sent",    label: t("controlCenter.filters.sent") },
    { value: "failed",  label: t("controlCenter.filters.failed") },
    { value: "blocked", label: t("controlCenter.filters.blocked") },
  ];

  // ── Consolidated kill switches (2 groups, 6 rows, 2 backends) ──────
  const smsSwitchGroup: SwitchRow[] = [
    { src: "config", key: "sms_enabled",          icon: MessageSquare, color: "text-blue-400",  label: t("controlCenter.smsEnabled"),          desc: t("controlCenter.smsEnabledDesc"), master: true },
    { src: "config", key: "sms_campaigns_enabled", icon: Megaphone,    color: "text-rose-400",  label: t("controlCenter.smsCampaignsEnabled"), desc: t("controlCenter.smsCampaignsDesc") },
    { src: "fin",    key: "verifyEnabled",        icon: KeyRound,      color: "text-blue-400",  label: t("finance.switches.verifyEnabled"),    desc: t("finance.switchesDesc.verifyEnabled"), dangerous: true },
    // ⚠️ dangerous: true ama TERS yonde — bunu KAPATMAK riskli (onay sarti kalkar),
    // acmak degil. handleFinSwitch zaten yalniz KAPATIRKEN onay soruyor.
    { src: "fin",    key: "smsApprovalEnabled",   icon: ShieldQuestion, color: "text-indigo-400", label: t("finance.switches.smsApprovalEnabled"), desc: t("finance.switchesDesc.smsApprovalEnabled"), dangerous: true },
  ];
  const otherSwitchGroup: SwitchRow[] = [
    { src: "config", key: "email_enabled",        icon: Mail,          color: "text-indigo-400",  label: t("controlCenter.emailEnabled"),                desc: t("controlCenter.emailEnabledDesc"), master: true },
    { src: "fin",    key: "uploadsEnabled",       icon: UploadCloud,   color: "text-emerald-400", label: t("finance.switches.uploadsEnabled"),           desc: t("finance.switchesDesc.uploadsEnabled"), dangerous: true },
    { src: "fin",    key: "imageTransformEnabled", icon: Sparkles,     color: "text-violet-400",  label: t("finance.switches.imageTransformEnabled"),    desc: t("finance.switchesDesc.imageTransformEnabled"), dangerous: false },
  ];

  const renderSwitchRow = (row: SwitchRow) => {
    const Icon = row.icon;
    const checked = row.src === "config"
      ? (config?.[row.key] ?? true)
      : (finConfig?.effective?.[row.key] ?? (row.key === "organizerSeesGuestDeletedMedia" ? false : true));
    const disabled = row.src === "config"
      ? savingConfig
      : (savingSwitch !== null || !finConfig);
    const onChange = (v: boolean) => {
      if (row.src === "config") void handleToggle(row.key, v);
      else handleFinSwitch(row.key, v);
    };
    const dangerous = row.src === "fin" && row.dangerous;
    return (
      <div key={row.key} className="flex items-center gap-4 px-5 py-4">
        <Icon className={cn("h-4 w-4 shrink-0", row.color)} />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-foreground flex items-center gap-1.5">
            {row.label}
            {row.src === "config" && row.master && (
              <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-500 border border-red-500/20">
                {t("controlCenter.masterBadge")}
              </span>
            )}
            {dangerous && <ShieldAlert className="h-3 w-3 text-amber-500 shrink-0" />}
          </p>
          <p className="text-xs text-muted-foreground">{row.desc}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={cn("text-xs font-semibold",
            checked ? "text-green-500" : "text-muted-foreground")}>
            {checked ? t("controlCenter.enabled") : t("controlCenter.disabled")}
          </span>
          <Toggle checked={checked} onChange={onChange} disabled={disabled} />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif italic text-3xl text-foreground">{t("controlCenter.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("controlCenter.description")}</p>
        </div>
        <button onClick={refresh}
          className="shrink-0 p-2.5 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-primary/5 hover:border-primary/30 transition-all cursor-pointer">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* ── Kill Switch ─────────────────────────────────── */}
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-3.5 border-b border-border bg-muted/30 flex items-center gap-2">
              {config?.sms_enabled && config?.email_enabled
                ? <ShieldCheck className="h-3.5 w-3.5 text-green-500" />
                : <ShieldOff className="h-3.5 w-3.5 text-destructive" />}
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {t("controlCenter.killSwitch")}
              </p>
              {saveSuccess && (
                <span className="ml-auto text-xs text-green-500 font-medium">{t("controlCenter.configSaved")}</span>
              )}
            </div>
            {/* Grup: SMS */}
            <div className="px-5 pt-4 pb-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {t("controlCenter.group.sms")}
              </p>
            </div>
            <div className="divide-y divide-border">
              {smsSwitchGroup.map(renderSwitchRow)}
            </div>
            {/* Grup: Diğer */}
            <div className="px-5 pt-4 pb-1 border-t border-border">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {t("controlCenter.group.other")}
              </p>
            </div>
            <div className="divide-y divide-border">
              {otherSwitchGroup.map(renderSwitchRow)}
            </div>
          </div>

          {/* ── Hesaplar ve gizlilik (platform politikaları) ── */}
          <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="cc-accounts-privacy">
            <div className="px-5 py-3.5 border-b border-border bg-muted/30 flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-primary" />
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {t("controlCenter.accountsPrivacy.title")}
              </p>
            </div>
            <div className="divide-y divide-border">
              <div className="flex items-center gap-4 px-5 py-4">
                <UserPlus className="h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-foreground">{t("siteAdmin.sales.selfServeTitle")}</p>
                  <p className="text-xs text-muted-foreground">
                    {selfServe === false ? t("siteAdmin.sales.selfServeOff") : t("siteAdmin.sales.selfServeOn")}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={cn("text-xs font-semibold", selfServe ? "text-green-500" : "text-muted-foreground")}>
                    {selfServe ? t("controlCenter.enabled") : t("controlCenter.disabled")}
                  </span>
                  <Toggle
                    checked={selfServe ?? false}
                    onChange={(v) => void handleSelfServe(v)}
                    disabled={selfServe === null || savingSelfServe}
                  />
                </div>
              </div>
              {renderSwitchRow({
                src: "fin",
                key: "organizerSeesGuestDeletedMedia",
                icon: EyeOff,
                color: "text-rose-400",
                label: t("finance.switches.organizerSeesGuestDeletedMedia"),
                desc: t("finance.switchesDesc.organizerSeesGuestDeletedMedia"),
                dangerous: false,
              })}
            </div>
          </div>

          {/* ── Stats ───────────────────────────────────────── */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {STAT_KEYS.map(({ key, label, color }) => (
                <div key={key} className="rounded-2xl border border-border bg-card px-4 py-4 text-center">
                  <p className={cn("text-3xl font-black", color)}>{stats[key]}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mt-1">{label}</p>
                </div>
              ))}
            </div>
          )}

          {/* ── Onay kuyruğu özeti (047) ─────────────────────
              Keşfedilebilirlik: onay kuyruğu AYRI bir sayfada (çapraz-wedding)
              yaşıyor; admin Kontrol Merkezi'ne bakarken bekleyen kampanya
              olduğunu görmezse kuyruk sessizce birikir. Sıfırken hiç çizilmez —
              boş bir kart gürültüden ibaret olurdu. */}
          {campaignStats && campaignStats.pendingApproval > 0 && (
            <AppLink
              to={paths.smsCenter}
              className="flex items-center gap-3 rounded-2xl border border-indigo-500/30 bg-indigo-500/5 px-5 py-4 hover:bg-indigo-500/10 transition-colors cursor-pointer"
            >
              <ShieldQuestion className="h-5 w-5 shrink-0 text-indigo-500" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">
                  {t("controlCenter.campaigns.pendingApproval", { count: campaignStats.pendingApproval })}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {t("controlCenter.campaigns.reviewLink")}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            </AppLink>
          )}

          {/* ── SMS Campaigns ───────────────────────────────── */}
          {campaignStats && (
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
              <div className="px-5 py-3.5 border-b border-border bg-muted/30 flex items-center gap-2">
                <Megaphone className="h-3.5 w-3.5 text-rose-400" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  {t("controlCenter.campaigns.title")}
                </p>
                {campaignStats.heldDue > 0 && (
                  <span className="ml-auto text-xs text-amber-500 font-semibold">
                    {t("controlCenter.campaigns.held", { count: campaignStats.heldDue })}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-border">
                {([
                  { key: "activeCampaigns", label: t("controlCenter.campaigns.active"),   color: "text-rose-400" },
                  { key: "sends24h",        label: t("controlCenter.campaigns.sends24h"), color: "text-blue-400" },
                  { key: "failed24h",       label: t("controlCenter.campaigns.failed24h"), color: "text-red-400" },
                  { key: "sends7d",         label: t("controlCenter.campaigns.sends7d"),  color: "text-indigo-400" },
                ] as { key: keyof CampaignStats; label: string; color: string }[]).map(({ key, label, color }) => (
                  <div key={key} className="px-4 py-4 text-center">
                    <p className={cn("text-3xl font-black", color)}>{campaignStats[key]}</p>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mt-1">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── SMS Harcama + Circuit Breaker ───────────────── */}
          {smsSpend && (
            <div className={cn(
              "rounded-2xl border bg-card overflow-hidden",
              smsSpend.breakerTripped ? "border-red-500/40" : "border-border",
            )}>
              <div className="px-5 py-3.5 border-b border-border bg-muted/30 flex items-center gap-2">
                <Wallet className="h-3.5 w-3.5 text-emerald-400" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  {t("controlCenter.spend.title")}
                </p>
              </div>

              {smsSpend.breakerTripped && (
                <div className="px-5 py-3.5 bg-red-500/10 border-b border-red-500/20 flex items-center gap-3 flex-wrap">
                  <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                  <p className="text-sm text-red-600 dark:text-red-400 flex-1 min-w-40">
                    {t("controlCenter.spend.tripped", { reason: smsSpend.trippedReason ?? "—" })}
                  </p>
                  <button onClick={handleResetBreaker} disabled={resettingBreaker}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-red-500 text-white text-xs font-semibold hover:bg-red-600 transition-colors cursor-pointer disabled:opacity-50">
                    {resettingBreaker ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                    <span>{t("controlCenter.spend.reset")}</span>
                  </button>
                </div>
              )}

              <div className="grid grid-cols-3 divide-x divide-border">
                <div className="px-4 py-4 text-center">
                  <p className="text-3xl font-black text-emerald-400 tabular-nums">{smsSpend.dailySegmentsUsed}<span className="text-sm text-muted-foreground font-semibold">/{smsSpend.dailyCap}</span></p>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mt-1">{t("controlCenter.spend.segments")}</p>
                </div>
                <div className="px-4 py-4 text-center">
                  <p className="text-3xl font-black text-amber-400 tabular-nums">${smsSpend.estUsd}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mt-1">{t("controlCenter.spend.estCost")}</p>
                </div>
                <div className="px-4 py-4 text-center">
                  <p className={cn("text-3xl font-black", smsSpend.breakerTripped ? "text-red-500" : "text-emerald-500")}>
                    {smsSpend.breakerTripped ? "✕" : "✓"}
                  </p>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mt-1">{t("controlCenter.spend.breaker")}</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Finansal Kontroller ─────────────────────────── */}
          {finConfig && (
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
              <div className="px-5 py-3.5 border-b border-border bg-muted/30 flex items-center gap-2">
                <Coins className="h-3.5 w-3.5 text-amber-500" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  {t("finance.title")}
                </p>
              </div>

              {/* Section 1 — Caps */}
              <div className="px-5 py-5">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">{t("finance.capsTitle")}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("finance.capsHint")}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveCaps}
                    disabled={!canSaveCaps}
                    className="shrink-0 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  >
                    {savingCaps ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    <span>{t("finance.save")}{capDirtyCount > 0 ? ` (${capDirtyCount})` : ""}</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {CAP_FIELDS.map((f) => {
                    const isOverride = Object.prototype.hasOwnProperty.call(finConfig.overrides ?? {}, f.key);
                    const dirty = capForm[f.key] !== capBaseline[f.key];
                    const parsed = displayToServer(f, capForm[f.key] ?? "");
                    const lo = f.min * (f.kind === "mb" ? BYTES_PER_MB : 1);
                    const hi = f.max * (f.kind === "mb" ? BYTES_PER_MB : 1);
                    const invalid = dirty && (parsed === null || parsed < lo || parsed > hi);
                    const unit = f.kind === "mb" ? t("finance.unitMb") : f.kind === "float" ? t("finance.unitUsd") : null;
                    const inputId = `fin-cap-${f.key}`;
                    return (
                      <div key={f.key} className="space-y-1.5">
                        <div className="flex items-center justify-between gap-2 min-h-5">
                          <label htmlFor={inputId} className="text-xs font-medium text-foreground truncate">
                            {t(`finance.caps.${f.key}`)}
                          </label>
                          {isOverride ? (
                            <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                              {t("finance.override")}
                            </span>
                          ) : (
                            <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground/60">
                              {t("finance.default")}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="relative flex-1">
                            <input
                              id={inputId}
                              type="number"
                              inputMode="decimal"
                              min={f.min}
                              max={f.max}
                              step={f.step}
                              value={capForm[f.key] ?? ""}
                              onChange={(e) => setCapForm((p) => ({ ...p, [f.key]: e.target.value }))}
                              className={cn(
                                "w-full rounded-xl border bg-background px-3 py-2 text-sm tabular-nums transition-colors",
                                "focus:outline-none focus:ring-2 focus:ring-primary/20",
                                unit ? "pr-11" : "",
                                invalid ? "border-red-500 focus:border-red-500" : "border-border focus:border-primary/50",
                              )}
                            />
                            {unit && (
                              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[10px] font-semibold uppercase text-muted-foreground">
                                {unit}
                              </span>
                            )}
                          </div>
                          {isOverride && (
                            <button
                              type="button"
                              onClick={() => void handleResetCap(f.key)}
                              disabled={resettingCap !== null}
                              aria-label={t("finance.resetField")}
                              title={t("finance.resetToDefault")}
                              className="shrink-0 p-2 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-primary/5 hover:border-primary/30 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                            >
                              {resettingCap === f.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                            </button>
                          )}
                        </div>
                        {invalid ? (
                          <p className="text-[10px] text-red-500">{t("finance.rangeHint", { min: f.min, max: f.max })}</p>
                        ) : f.desc ? (
                          <p className="text-[10px] leading-snug text-muted-foreground">{t(`finance.capsDesc.${f.key}`)}</p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Section 2 — Monitoring */}
              <div className="px-5 py-5 border-t border-border">
                <div className="flex items-center gap-2 mb-4">
                  <Gauge className="h-3.5 w-3.5 text-primary/70" />
                  <h3 className="text-sm font-semibold text-foreground">{t("finance.monitoringTitle")}</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {([
                    { label: t("finance.mon.verify"),   used: finConfig.monitoring.verifyUsedToday,      cap: finConfig.effective.verifyDailyCap,      bar: "bg-blue-500" },
                    { label: t("finance.mon.segments"), used: finConfig.monitoring.smsSegmentsUsedToday, cap: finConfig.effective.smsSegmentDailyCap,  bar: "bg-emerald-500" },
                  ]).map(({ label, used, cap, bar }) => {
                    const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
                    return (
                      <div key={label} className="rounded-2xl border border-border bg-muted/20 px-4 py-3.5">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
                          <p className="text-sm font-bold tabular-nums text-foreground">
                            {used}<span className="text-muted-foreground font-medium">/{cap}</span>
                          </p>
                        </div>
                        <div className="mt-2 h-1.5 w-full rounded-full bg-border overflow-hidden">
                          <div className={cn("h-full rounded-full transition-all", pct >= 90 ? "bg-red-500" : bar)} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 flex items-center gap-2.5 rounded-2xl border border-border bg-muted/20 px-4 py-3">
                  {finConfig.monitoring.spendBreaker.tripped
                    ? <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                    : <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0" />}
                  <p className="text-xs text-muted-foreground flex-1">
                    {t("finance.mon.breaker")}
                    {" — "}
                    <span className={cn("font-semibold", finConfig.monitoring.spendBreaker.tripped ? "text-red-500" : "text-emerald-500")}>
                      {finConfig.monitoring.spendBreaker.tripped ? t("finance.mon.breakerTripped") : t("finance.mon.breakerOk")}
                    </span>
                    {finConfig.monitoring.spendBreaker.tripped && finConfig.monitoring.spendBreaker.reason ? (
                      <span>{` (${finConfig.monitoring.spendBreaker.reason})`}</span>
                    ) : null}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Logs ────────────────────────────────────────── */}
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-3.5 border-b border-border bg-muted/30 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Bell className="h-3.5 w-3.5 text-primary/60" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("controlCenter.logs")}</p>
              </div>
              <div className="flex items-center rounded-lg border border-border overflow-hidden ml-auto">
                {TYPE_OPTS.map(({ value, label }) => (
                  <button key={value} onClick={() => setTypeFilter(value)}
                    className={cn("px-3 py-1 text-xs font-medium transition-colors cursor-pointer",
                      typeFilter === value ? "bg-primary text-white" : "text-muted-foreground hover:bg-primary/5")}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex items-center rounded-lg border border-border overflow-hidden">
                {STATUS_OPTS.map(({ value, label }) => (
                  <button key={value} onClick={() => setStatusFilter(value)}
                    className={cn("px-3 py-1 text-xs font-medium transition-colors cursor-pointer",
                      statusFilter === value ? "bg-primary text-white" : "text-muted-foreground hover:bg-primary/5")}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {logsLoading && logs.length === 0 ? (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <Bell className="h-8 w-8 text-primary/20" />
                <p className="text-sm text-muted-foreground">{t("controlCenter.noLogs")}</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead className="border-b border-border bg-muted/20">
                      <tr>
                        {[
                          t("controlCenter.createdAt"),
                          t("controlCenter.type"),
                          t("controlCenter.recipient"),
                          t("controlCenter.weddingId"),
                          t("controlCenter.status"),
                          t("controlCenter.error"),
                        ].map((h) => (
                          <th key={h} className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {logs.map((log) => (
                        <tr key={log.id} className="hover:bg-primary/5 transition-colors">
                          <td className="px-5 py-3 text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(log.created_at).toLocaleString(i18n.language)}
                          </td>
                          <td className="px-5 py-3">
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
                              {log.type === "sms"
                                ? <MessageSquare className="h-3.5 w-3.5 text-blue-400" />
                                : <Mail className="h-3.5 w-3.5 text-indigo-400" />}
                              <span className={log.type === "sms" ? "text-blue-400" : "text-indigo-400"}>
                                {log.type?.toUpperCase() ?? "—"}
                              </span>
                            </span>
                          </td>
                          <td className="px-5 py-3 font-mono text-xs text-foreground">{log.recipient}</td>
                          <td className="px-5 py-3 font-mono text-xs text-muted-foreground">
                            {log.wedding_id
                              ? <span className="bg-muted px-1.5 py-0.5 rounded">{log.wedding_id.slice(0, 8)}…</span>
                              : <span className="text-muted-foreground/40">—</span>}
                          </td>
                          <td className="px-5 py-3">
                            <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border",
                              STATUS_STYLE[log.status] ?? STATUS_STYLE.failed)}>
                              {log.status}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-xs text-muted-foreground max-w-[180px] truncate">
                            {log.error ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {hasMore && (
                  <div className="flex justify-center px-5 py-4 border-t border-border">
                    {logsLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    ) : (
                      <button
                        onClick={loadMore}
                        className="px-5 py-2 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/30 hover:bg-primary/5 transition-all cursor-pointer"
                      >
                        {t("controlCenter.loadMore")}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Dangerous switch confirm — maintenance purge modal pattern, red warning */}
      {confirmSwitch && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setConfirmSwitch(null)} />
          <div className="relative w-full max-w-sm rounded-2xl bg-card border border-red-500/40 shadow-2xl overflow-hidden">
            <div className="px-5 py-4 bg-red-500/10 border-b border-red-500/20 flex items-center gap-2.5">
              <ShieldAlert className="h-4 w-4 text-red-500 shrink-0" />
              <h3 className="text-sm font-semibold text-red-600 dark:text-red-400">{t("finance.confirm.title")}</h3>
            </div>
            <div className="px-5 py-4 space-y-2">
              <p className="text-sm text-foreground">
                {confirmSwitch === "verifyEnabled"
                  ? t("finance.confirm.verifyWarning")
                  : confirmSwitch === "smsApprovalEnabled"
                    ? t("finance.confirm.approvalWarning")
                    : t("finance.confirm.uploadsWarning")}
              </p>
              <p className="text-xs text-muted-foreground">{t("finance.confirm.detail")}</p>
            </div>
            <div className="flex gap-3 px-5 pb-5 pt-1">
              <button
                type="button"
                onClick={() => setConfirmSwitch(null)}
                disabled={savingSwitch !== null}
                className="flex-1 rounded-xl border border-border py-2.5 text-sm font-medium hover:bg-muted transition-colors cursor-pointer disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={() => {
                  const key = confirmSwitch;
                  setConfirmSwitch(null);
                  void performSwitchPatch(key, false);
                }}
                disabled={savingSwitch !== null}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-red-500 py-2.5 text-sm font-semibold text-white hover:bg-red-600 transition-colors cursor-pointer disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
              >
                {savingSwitch !== null && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                <span>{t("finance.confirm.turnOff")}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} onDone={() => setToast(null)} />}
    </div>
  );
}
